<template>
  <v-data-table
    :headers="formattedHeaders"
    :footer-props="mergedFooterProps"
    v-bind="{ ...$props, ...$attrs }"
    v-on="$listeners"
  >
    <!-- Header slot option -->
    <template v-for="name in headerSlots" #[`header.${name}`]>
      <div :key="name" :class="[`text-${getHeaderProperty(name, 'align') || 'left'}`]">
        <slot :name="`header.${name}`" />
      </div>
    </template>

    <!-- Group Header slot option -->
    <template v-if="$scopedSlots['group.header']" #group.header="{ group, isOpen, toggle, remove }">
      <td :colspan="headers.length">
        <slot name="group.header" :group="group" :isOpen="isOpen" :toggle="toggle" :remove="remove" />
      </td>
    </template>

    <template #item="{ item, index }">
      <v-menu
        :value="contextmenuId === item.id"
        :position-x="contextmenuX"
        :position-y="contextmenuY"
        absolute
        offset-y
        close-on-click
      >
        <template #activator="{ on }">
          <v-hover v-slot="{ hover }">
            <tr
              :class="[{ pointer: shouldRowHavePointer }, getItemClassList(item)]"
              @click.exact="onClickRow('exact', item)"
              @click.meta="onClickRow('meta', item)"
              @click.ctrl="onClickRow('ctrl', item)"
              @contextmenu="onContextmenu($event, on, item)"
            >
              <!-- Row slot option -->
              <slot name="item" :item="item" :index="index">
                <td
                  v-for="{ value, align } in formattedHeadersWithoutActions"
                  :key="value"
                  :class="[`text-${align || 'left'}`]"
                >
                  <!-- Individual cell slot option -->
                  <slot :name="`item.${value}`" :item="item" :value="item[value]" :index="index">
                    {{ item[value] }}
                  </slot>
                </td>
              </slot>

              <!-- Action menu slot option -->
              <td v-if="hasActions" class="text-right">
                <div v-show="hover">
                  <more-menu :small="smallAction">
                    <slot name="actions" :item="item" :index="index" />
                  </more-menu>
                </div>
              </td>
            </tr>
          </v-hover>
        </template>
        <v-list class="py-0" color="secondary" dark dense>
          <v-list-item v-if="route" @click="openLink(item, true)">
            <v-list-item-title>
              <v-icon small left>launch</v-icon>
              <span>Open in new tab</span>
            </v-list-item-title>
          </v-list-item>

          <!-- Contextmenu slot option -->
          <slot name="contextmenu" :item="item" :index="index" />
        </v-list>
      </v-menu>
    </template>
  </v-data-table>
</template>

<script>
// Helpers
import { merge } from 'lodash-es'
// Components
import MoreMenu from '@/components/more-menu'

export default {
  name: 'CustomTable',
  components: { MoreMenu },
  props: {
    route: {
      type: Object,
      default: null,
      validator(route) {
        return !route || (route.name && route.params && Array.isArray(route.params))
      },
    },
    smallAction: {
      type: Boolean,
      default: false,
    },
    footerProps: {
      type: Object,
      default: () => ({}),
    },
  },
  data: () => ({
    contextmenuId: null,
    contextmenuX: 0,
    contextmenuY: 0,
    showContextMenu: false,
  }),
  computed: {
    formattedHeaders() {
      const actionsHeader = this.headers.find(({ value }) => value === 'actions') || {}
      const mergedActionsHeader = merge(
        { text: '', value: 'actions', sortable: false, align: 'right', width: 70 },
        actionsHeader
      )

      return [
        ...this.headers.filter(({ value }) => value !== 'actions'),
        this.hasActions ? mergedActionsHeader : null,
      ].filter(Boolean)
    },
    formattedHeadersWithoutActions() {
      return this.formattedHeaders.filter(({ value }) => value !== 'actions')
    },
    hasActions() {
      return !!(this.headers.find(({ value }) => value === 'actions') && this.$scopedSlots.actions)
    },
    headers() {
      return this.$attrs.headers || this.$props.headers || []
    },
    headerSlots() {
      return Object.keys(this.$scopedSlots)
        .filter(key => key.includes('header.'))
        .map(key => key.split('header.')[1])
    },
    mergedFooterProps() {
      const passedFooterProps = this.$props.footerProps || {}
      return {
        ...passedFooterProps,
        'show-first-last-page':
          'show-first-last-page' in passedFooterProps ? passedFooterProps['show-first-last-page'] : true,
      }
    },
    shouldRowHavePointer() {
      return this.route || this.$listeners['click-item']
    },
  },
  methods: {
    getHeaderProperty(value, property) {
      return this.headers.find(header => header.value === value)?.[property]
    },
    getItemClassList(item) {
      if (!this.$props.itemClass) return ''
      if (typeof this.$props.itemClass === 'string') return this.$props.itemClass
      return this.$props.itemClass(item)
    },
    getItemRouteParams(item) {
      try {
        return this.route.params.reduce((params, param) => ({ ...params, [param]: item[param] }), {})
      } catch (e) {
        this.$notify({ type: 'error', text: 'Route not configured correctly.' })
      }
    },
    onClickRow(type, payload) {
      const openInNewTab = ['meta', 'ctrl'].includes(type)
      if (this.route) this.openLink(payload, openInNewTab)
      this.$emit('click-item', payload)
    },
    onContextmenu(e, on, item) {
      if (this.$scopedSlots.contextmenu || this.route) {
        e.preventDefault()
        if (this.contextmenuId === item.id) {
          this.contextmenuId = null
        } else {
          this.contextmenuId = item.id
          this.contextmenuX = e.x
          this.contextmenuY = e.y
        }
      } else {
        this.$emit('contextmenu', item)
      }
    },
    openLink(item, newTab = false) {
      try {
        const route = this.$router.resolve({ name: this.route.name, params: this.getItemRouteParams(item) })
        if (newTab) {
          window.open(route.href, '_blank')
        } else {
          this.$router.push(route.route)
        }
      } catch (e) {
        this.$notify({ type: 'error', text: 'Route not configured correctly.' })
      }
    },
  },
}
</script>
<style lang="scss">
.pointer {
  cursor: pointer;
}
</style>
